(() => {
  const theme = (window.SxTheme = window.SxTheme || {});

  theme.events = {
    cartUpdated: 'sx:cart-updated',
    openCart: 'sx:open-cart',
    variantChanged: 'sx:variant-changed',
    quantity: 'sx:pdp-quantity',
  };

  theme.emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

  theme.reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  theme.fillTemplate = (template, values) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  theme.formatMoney = (cents, format = theme.moneyFormat || '{{amount}} TL') => {
    const value = Number(cents || 0) / 100;
    const withDelimiters = (amount, decimals, thousands, decimal) => {
      const fixed = amount.toFixed(decimals);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        default:
          return withDelimiters(value, 2, ',', '.');
      }
    });
  };

  /* ---------- Morse (TR) — shared by the personalization block and the quick-add sheet ---------- */
  theme.morse = {
    table: {
      A: '.-', B: '-...', C: '-.-.', Ç: '-.-..', D: '-..', E: '.', F: '..-.', G: '--.', Ğ: '--.-.', H: '....',
      I: '..', İ: '.-..-', J: '.---', K: '-.-', L: '.-..', M: '--', N: '-.', O: '---', Ö: '---.', P: '.--.',
      Q: '--.-', R: '.-.', S: '...', Ş: '.--..', T: '-', U: '..-', Ü: '..--', V: '...-', W: '.--', X: '-..-',
      Y: '-.--', Z: '--..', 0: '-----', 1: '.----', 2: '..---', 3: '...--', 4: '....-', 5: '.....',
      6: '-....', 7: '--...', 8: '---..', 9: '----.',
    },
    disallowed: /[^A-Z0-9ÇĞİÖŞÜ ]/g,
    clean(raw) {
      return String(raw || '')
        .toLocaleUpperCase('tr-TR')
        .replace(theme.morse.disallowed, '')
        .replace(/ {2,}/g, ' ')
        .replace(/^ /, '');
    },
    encode(text) {
      return theme.morse
        .clean(text)
        .split(' ')
        .filter(Boolean)
        .map((word) =>
          Array.from(word)
            .map((char) => theme.morse.table[char] || '')
            .filter(Boolean)
            .join(' '),
        )
        .join(' / ');
    },
  };

  /* ---------- Scroll lock (ref-counted so stacked overlays don't unlock early) ---------- */
  let lockCount = 0;
  theme.lockScroll = () => {
    lockCount += 1;
    if (lockCount === 1) {
      const gap = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--sx-scrollbar-gap', `${gap}px`);
      document.documentElement.classList.add('sx-scroll-locked');
    }
  };
  theme.unlockScroll = () => {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.documentElement.classList.remove('sx-scroll-locked');
  };

  /* ---------- Focus trap ---------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  theme.trapFocus = (container) => {
    const previous = document.activeElement;
    const onKey = (event) => {
      if (event.key !== 'Tab') return;
      const items = Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKey);
    const target = container.querySelector('[autofocus]') || container.querySelector(FOCUSABLE) || container;
    requestAnimationFrame(() => target.focus({ preventScroll: true }));
    return () => {
      container.removeEventListener('keydown', onKey);
      if (previous && typeof previous.focus === 'function') previous.focus({ preventScroll: true });
    };
  };

  /* ---------- Cart API ---------- */
  theme.cartSections = () =>
    Array.from(document.querySelectorAll('[data-cart-section]'))
      .map((el) => el.dataset.cartSection)
      .filter((id, index, all) => id && all.indexOf(id) === index);

  const postJSON = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || theme.strings?.cartError || 'Cart error');
      error.data = data;
      throw error;
    }
    return data;
  };

  theme.renderSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (typeof html !== 'string') return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      document.querySelectorAll(`[data-cart-section="${id}"]`).forEach((target) => {
        const key = target.dataset.cartTarget;
        const source = key ? doc.querySelector(`[data-cart-target="${key}"]`) : doc.querySelector('[data-cart-section]');
        if (source) target.innerHTML = source.innerHTML;
      });
    });
  };

  theme.cart = {
    async add(items) {
      const sections = theme.cartSections();
      const data = await postJSON(`${theme.routes.cartAdd}.js`, { items, sections, sections_url: window.location.pathname });
      theme.renderSections(data.sections);
      theme.emit(theme.events.cartUpdated, { action: 'add', data });
      return data;
    },
    async change(line, quantity) {
      const sections = theme.cartSections();
      const data = await postJSON(`${theme.routes.cartChange}.js`, { line, quantity, sections, sections_url: window.location.pathname });
      theme.renderSections(data.sections);
      theme.emit(theme.events.cartUpdated, { action: 'change', data });
      return data;
    },
    async update(body) {
      const sections = theme.cartSections();
      const data = await postJSON(`${theme.routes.cartUpdate}.js`, { ...body, sections, sections_url: window.location.pathname });
      theme.renderSections(data.sections);
      theme.emit(theme.events.cartUpdated, { action: 'update', data });
      return data;
    },
    afterAdd() {
      if (theme.cartType === 'page') {
        window.location.href = theme.routes.cart;
      } else {
        theme.emit(theme.events.openCart);
      }
    },
  };

  /* ---------- Scroll reveal ---------- */
  const setupReveal = () => {
    const enabled = document.documentElement.dataset.sxAnim !== 'off' && !theme.reducedMotion() && 'IntersectionObserver' in window;
    if (!enabled) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('sx-reveal-in');
          entry.target.classList.remove('sx-reveal-pending');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    );
    const observe = (root) => {
      root.querySelectorAll('[data-reveal]:not(.sx-reveal-in):not(.sx-reveal-pending)').forEach((el, index) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) return;
        el.style.setProperty('--reveal-delay', `${Math.min(index % 4, 3) * 80}ms`);
        el.classList.add('sx-reveal-pending');
        observer.observe(el);
      });
    };
    observe(document);
    document.addEventListener('shopify:section:load', (event) => observe(event.target));
  };

  /* ---------- Modal (native <dialog>) ---------- */
  class SxModal extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.bound = true;
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      /* Esc closes a modal dialog natively; intercept it so cleanup runs through one path. */
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('close', () => this.cleanup());
      this.querySelectorAll('[data-modal-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
    }

    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      if (this.parentElement !== document.body && this.hasAttribute('data-portal')) document.body.appendChild(this);
      this.dialog.showModal();
      theme.lockScroll();
      this.locked = true;
      this.opener = opener;
      this.releaseFocus = theme.trapFocus(this.dialog);
    }

    close() {
      if (this.dialog?.open) this.dialog.close();
      /* Not every engine dispatches the dialog "close" event reliably, and a missed one would
         leave the page scroll-locked for good. cleanup() is idempotent, so run it here too. */
      this.cleanup();
    }

    cleanup() {
      if (!this.locked) return;
      this.locked = false;
      theme.unlockScroll();
      if (this.releaseFocus) this.releaseFocus();
      this.releaseFocus = null;
    }
  }
  if (!customElements.get('sx-modal')) customElements.define('sx-modal', SxModal);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-modal-open]');
    if (!trigger) return;
    const modal = document.getElementById(trigger.dataset.modalOpen);
    if (modal && typeof modal.open === 'function') {
      event.preventDefault();
      modal.open(trigger);
    }
  });

  /* ---------- Copy to clipboard ---------- */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-copy]');
    if (!button) return;
    try {
      await navigator.clipboard.writeText(button.dataset.copy);
      button.classList.add('is-copied');
      const label = button.querySelector('[data-copy-label]');
      const original = label?.textContent;
      if (label && button.dataset.copiedText) label.textContent = button.dataset.copiedText;
      setTimeout(() => {
        button.classList.remove('is-copied');
        if (label && original) label.textContent = original;
      }, 2000);
    } catch (error) {
      button.classList.remove('is-copied');
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupReveal);
  } else {
    setupReveal();
  }
})();
